import React, { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Button, Form, Segment } from "semantic-ui-react";
import SidebarWithHeader from "../../../../../../components/global_components/SidebarWithHeader";
import { PermitsContact } from "../../../../types/PermitsContact";
import { PermitsCustomFieldTypesNames } from "../../../../../../constants/PermitsCustomFieldTypesNames";
import { ContactInfoOptions } from "../../../../types/ContactInfoOptions";
import useApiCallWithToast from "../../../../../../hooks/useApiCallWithToast";
import { putPermitContact, postPermitContact } from "../../../../../../queries/permits";
import { queryCache } from "react-query";
import { useParams } from "react-router-dom";
import { ContactFieldsV2 } from "../../../../../contacts/components/ContactFieldsV2";
import {
  buildNewPermitContactPayload,
  buildUpdatePermitContactPayload,
} from "../../../../utils/buildPermitsContactPayload";
import useNetworkHeaders from "../../../../../../hooks/useNetworkHeaders";

interface IContactSidebar {
  contact: PermitsContact;
  showEditContactSidebar: boolean;
  setShowEditContactSidebar: (value: boolean) => void;
  contactInfoOptions: ContactInfoOptions;
  contactDoesntExist?: boolean;
  customFieldType?: number;
  customFieldName?: string;
  permitsExtensionDefId?: number | null;
}

/**
 * Sidebar component for editing contact information
 *
 * @param props
 * @returns
 */
export const EditInfoContactSidebar = (props: IContactSidebar) => {
  const {
    contact,
    contactDoesntExist = false,
    showEditContactSidebar,
    setShowEditContactSidebar,
    customFieldType,
    contactInfoOptions,
    customFieldName,
    permitsExtensionDefId,
  } = props;

  const formContext = useForm();
  const networkHeaders = useNetworkHeaders();
  const isAdditionalContact = customFieldType === PermitsCustomFieldTypesNames.ADDITIONAL_CONTACTS;
  const { id: permitId } = useParams<{ id: string }>();
  const contactId = useMemo(() => contact?.id, [contact]);

  // fields used for the payload to edit/create contact on the backend
  const defaultContactFields = {
    contactType: contact?.contactType,
    firstName: contact?.firstName,
    lastName: contact?.lastName,
    email: contact?.email,
    phone: contact?.phone,
    licenseNumber: contact?.licenseNumber,
    companyName: contact?.companyName,
    address: contact?.address,
    address2: contact?.address2,
    city: contact?.city,
    state: contact?.state,
    zip: contact?.zip,
    countryId: contact?.countryId,
    physicalAddress: contact?.physicalAddress,
    physicalAddress2: contact?.physicalAddress2,
    physicalCity: contact?.physicalCity,
    physicalState: contact?.physicalState,
    physicalZip: contact?.physicalZip,
    homeCountryId: contact?.homeCountryId,
    sameMailAndPhysicalAddress: contact?.sameMailAndPhysicalAddress,
  };

  /**
   * Save changes to a contact or creates a new contact if it doesn't exist
   *
   * @param data
   */
  const saveEditContactChanges = async (data: any) => {
    // Edit existing permit contact
    if (contactId) {
      const updatePayload = buildUpdatePermitContactPayload(data);
      return await updatePermitContact(updatePayload);
    }

    // Create new permit contact
    const createPayload = buildNewPermitContactPayload(
      {
        ...data,
        contactId: undefined,
        permitId,
        permitsExtensionDefId,
        customFieldType,
        ...(!isAdditionalContact ? { contactType: customFieldName } : {}),
      },
      false,
    );
    return await createPermitContact(createPayload);
  };

  // mutation to update contact
  const { run: updatePermitContact, loading: isUpdatePermitContactLoading } = useApiCallWithToast({
    apiCall: () => putPermitContact(networkHeaders, contactId),
    onSuccess: () => {
      queryCache.invalidateQueries(["conversation", permitId.toString()]);
      setShowEditContactSidebar(false);
    },
    queryKey: "permits",
    successMessage: "Contact updated successfully",
  });

  // mutation to create contact
  const { run: createPermitContact, loading: isCreatePermitContactLoading } = useApiCallWithToast({
    apiCall: () => postPermitContact(networkHeaders),
    onSuccess: () => {
      queryCache.invalidateQueries(["conversation", permitId.toString()]);
      setShowEditContactSidebar(false);
    },
    queryKey: "permits",
    successMessage: "Contact added successfully",
  });

  return (
    <SidebarWithHeader
      headerText={contactDoesntExist ? "Add Contact Information" : "Edit Submitted Info"}
      visible={showEditContactSidebar}
      closeSidebar={() => setShowEditContactSidebar(false)}
    >
      {defaultContactFields && (
        <Segment basic style={{ paddingTop: 0 }}>
          <Form>
            <FormProvider {...formContext}>
              <ContactFieldsV2
                physicalAddressPrefix='physical'
                visibilityOptions={contactInfoOptions}
                defaultContactFields={defaultContactFields}
                showLicenseAndCompanyFields={true}
                showMailingAddressHeader={false}
                showContactTypeField={isAdditionalContact}
                autofillCountry={!contactId} // autofill country flag if we're not editing an existing contact
                saveButton={
                  <Button
                    primary
                    loading={isUpdatePermitContactLoading || isCreatePermitContactLoading}
                    color='blue'
                    fluid
                    onClick={formContext.handleSubmit(saveEditContactChanges)}
                    style={{ marginTop: 24 }}
                  >
                    Save Changes
                  </Button>
                }
              />
            </FormProvider>
          </Form>
        </Segment>
      )}
    </SidebarWithHeader>
  );
};

export default EditInfoContactSidebar;
